import React, { useCallback, useEffect, useMemo, useState, Fragment } from 'react';
import { ReactFlow, Controls, Background, useNodesState, useEdgesState, addEdge, Connection, Edge, Node, BackgroundVariant, MiniMap } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useNavigate } from 'react-router-dom';
import ServiceNode from '../components/canvas/ServiceNode';
import EngineNode from '../components/canvas/EngineNode';
import ProviderNode from '../components/canvas/ProviderNode';
import AnimatedEdge from '../components/canvas/AnimatedEdge';
import { ServiceDetailCanvas } from '../components/canvas/ServiceDetailCanvas';
import { RightPanel } from '../components/RightPanel';
import { AppBadge } from '../components/base/AppBadge';
import { AppButton } from '../components/base/AppButton';
import { Activity, TrendingUp, Search, Cpu, FileText, Settings, ChevronDown, SlidersHorizontal, Calendar } from 'lucide-react';
// --- Data Types ---
type HealthStatus = 'healthy' | 'warning' | 'critical';
interface ServiceData {
  id: string;
  name: string;
  type: string;
  model: string;
  health: HealthStatus;
  cost: number;
  compliance: number;
  co2: string;
  description: string;
  requestsPerDay: number;
  tokens: string;
  businessUnit: string;
  vendor: string;
}
// --- Mock Data ---
const SERVICES_DATA: ServiceData[] = [{
  id: 's1',
  name: 'Customer Assistant',
  type: 'Chatbot',
  model: 'GPT-4o',
  health: 'healthy',
  cost: 45,
  compliance: 92,
  co2: '1.2kg',
  description: 'Primary customer support agent handling L1 queries.',
  requestsPerDay: 1250,
  tokens: '450K',
  businessUnit: 'Customer Care',
  vendor: 'Dmood'
}, {
  id: 's2',
  name: 'Smart Matcher',
  type: 'Matcher',
  model: 'GPT-4 Turbo',
  health: 'healthy',
  cost: 32,
  compliance: 88,
  co2: '0.8kg',
  description: 'Matches user intent to relevant knowledge base articles.',
  requestsPerDay: 5500,
  tokens: '1.2M',
  businessUnit: 'Customer Care',
  vendor: 'Dmood'
}, {
  id: 's3',
  name: 'Content Moderator',
  type: 'Moderator',
  model: 'Claude 3.5',
  health: 'warning',
  cost: 18,
  compliance: 71,
  co2: '0.6kg',
  description: 'Filters and flags inappropriate content in real-time.',
  requestsPerDay: 800,
  tokens: '200K',
  businessUnit: 'Legal & Compliance',
  vendor: 'Dmood'
}, {
  id: 's4',
  name: 'AI Tutor',
  type: 'Tutor',
  model: 'GPT-4 Turbo',
  health: 'healthy',
  cost: 28,
  compliance: 95,
  co2: '0.7kg',
  description: 'Internal training assistant for employee onboarding.',
  requestsPerDay: 560,
  tokens: '300K',
  businessUnit: 'HR & Training',
  vendor: 'Dmood'
}, {
  id: 's5',
  name: 'Profile Enhancer',
  type: 'Enhancer',
  model: 'GPT-4',
  health: 'warning',
  cost: 38,
  compliance: 68,
  co2: '1.5kg',
  description: 'Enriches user profiles with inferred data points.',
  requestsPerDay: 4200,
  tokens: '890K',
  businessUnit: 'Marketing',
  vendor: 'Dmood'
}, {
  id: 's6',
  name: 'Meeting Notes AI',
  type: 'Note-taker',
  model: 'Whisper-1',
  health: 'critical',
  cost: 23,
  compliance: 45,
  co2: '0.9kg',
  description: 'Transcribes and summarizes internal meetings.',
  requestsPerDay: 120,
  tokens: 'N/A',
  businessUnit: 'Operations',
  vendor: 'Dmood'
}, {
  id: 's7',
  name: 'Weekly Digest',
  type: 'Summarizer',
  model: 'GPT-3.5 Turbo',
  health: 'healthy',
  cost: 8,
  compliance: 90,
  co2: '0.3kg',
  description: 'Generates weekly summary digests for stakeholders.',
  requestsPerDay: 4500,
  tokens: '1.5M',
  businessUnit: 'Operations',
  vendor: 'Dmood'
}, {
  id: 's8',
  name: 'Report Generator',
  type: 'Reporter',
  model: 'GPT-4 Turbo',
  health: 'healthy',
  cost: 22,
  compliance: 96,
  co2: '1.0kg',
  description: 'Automated generation of compliance and performance reports.',
  requestsPerDay: 150,
  tokens: '500K',
  businessUnit: 'Finance',
  vendor: 'Dmood'
}];
const PROVIDERS_DATA = [{
  id: 'p1',
  name: 'OpenAI',
  percentage: 68,
  models: ['GPT-4o', 'GPT-4 Turbo'],
  color: '#10a37f'
}, {
  id: 'p2',
  name: 'Anthropic',
  percentage: 22,
  models: ['Claude 3.5'],
  color: '#d4a574'
}, {
  id: 'p3',
  name: 'Azure AI',
  percentage: 7,
  models: ['GPT-4'],
  color: '#0078d4'
}, {
  id: 'p4',
  name: 'Google',
  percentage: 3,
  models: ['Gemini Pro'],
  color: '#4285f4'
}];
const MODEL_TO_PROVIDER: Record<string, string> = {
  'GPT-4o': 'p1',
  'GPT-4 Turbo': 'p1',
  'GPT-4': 'p1',
  'GPT-3.5 Turbo': 'p1',
  'Whisper-1': 'p1',
  'Claude 3.5': 'p2',
  'Gemini Pro': 'p4'
};
const initialNodes: Node[] = [{
  id: 'engine',
  type: 'engineNode',
  position: {
    x: 1250,
    y: 580
  },
  data: {
    label: 'DMOOD Engine'
  },
  draggable: true
}, ...SERVICES_DATA.map((service, index) => ({
  id: service.id,
  type: 'serviceNode',
  position: {
    x: index * 450,
    y: 200
  },
  data: {
    label: service.name,
    type: service.type,
    cost: service.cost,
    health: service.health,
    model: service.model,
    businessUnit: service.businessUnit,
    vendor: service.vendor
  },
  draggable: true
})), ...PROVIDERS_DATA.map((provider, index) => ({
  id: provider.id,
  type: 'providerNode',
  position: {
    x: 850 + index * 450,
    y: 1100
  },
  data: {
    label: provider.name,
    percentage: provider.percentage,
    models: provider.models,
    color: provider.color
  },
  draggable: true
}))];
const getIntensity = (requestsPerDay: number) => Math.max(1, Math.min(8, Math.round(requestsPerDay / 600)));
const getCostIntensity = (cost: number) => Math.max(1, Math.min(8, Math.round(cost / 6)));
const initialEdges: Edge[] = [...SERVICES_DATA.map(service => ({
  id: `e-${service.id}-engine`,
  source: service.id,
  target: 'engine',
  type: 'animatedEdge',
  data: {
    intensity: getIntensity(service.requestsPerDay)
  },
  style: {
    stroke: 'rgba(255, 255, 255, 0.20)',
    strokeWidth: 1.5
  },
  animated: true
})), ...PROVIDERS_DATA.map(provider => ({
  id: `e-engine-${provider.id}`,
  source: 'engine',
  target: provider.id,
  type: 'animatedEdge',
  data: {
    intensity: Math.max(1, Math.round(provider.percentage / 15))
  },
  style: {
    stroke: 'rgba(255, 255, 255, 0.20)',
    strokeWidth: 1.5
  },
  animated: true
}))];
export function AIMapPage() {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);
  const [focusedProviderId, setFocusedProviderId] = useState<string | null>(null);
  const [detailNodeId, setDetailNodeId] = useState<string | null>(null);
  const [panelTab, setPanelTab] = useState<'overview' | 'compliance' | 'consumi'>('overview');
  const [flowMode, setFlowMode] = useState<'utilizzo' | 'consumi'>('utilizzo');
  const [flowDropdownOpen, setFlowDropdownOpen] = useState(false);
  const [healthFilter, setHealthFilter] = useState<Set<HealthStatus>>(new Set());
  const [dateRange, setDateRange] = useState('last_month');
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // NEW: Drill-down state for service detail canvas
  const [drillServiceId, setDrillServiceId] = useState<string | null>(null);
  // Get the drilled service data
  const drilledService = useMemo(() => SERVICES_DATA.find(s => s.id === drillServiceId) || null, [drillServiceId]);
  const dateOptions = [{
    id: 'last_week',
    label: 'Ultima settimana'
  }, {
    id: 'last_month',
    label: 'Ultimo mese'
  }, {
    id: 'last_quarter',
    label: 'Ultimo trimestre'
  }, {
    id: 'last_semester',
    label: 'Ultimo semestre'
  }, {
    id: 'last_year',
    label: 'Anno'
  }, {
    id: 'custom',
    label: 'Personalizzato'
  }];
  const selectedDateLabel = dateOptions.find(d => d.id === dateRange)?.label || 'Ultimo mese';
  const toggleHealthFilter = useCallback((status: HealthStatus) => {
    setHealthFilter(prev => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);else next.add(status);
      return next;
    });
    setFocusedNodeId(null);
  }, []);
  const filteredServiceIds = useMemo(() => {
    if (healthFilter.size === 0) return null;
    return new Set(SERVICES_DATA.filter(s => healthFilter.has(s.health)).map(s => s.id));
  }, [healthFilter]);
  const filteredProviderIds = useMemo(() => {
    if (!filteredServiceIds) return null;
    const providerIds = new Set<string>();
    SERVICES_DATA.filter(s => filteredServiceIds.has(s.id)).forEach(s => {
      const pid = MODEL_TO_PROVIDER[s.model];
      if (pid) providerIds.add(pid);
    });
    return providerIds;
  }, [filteredServiceIds]);
  const providerServiceIds = useMemo(() => {
    if (!focusedProviderId) return null;
    return new Set(SERVICES_DATA.filter(s => MODEL_TO_PROVIDER[s.model] === focusedProviderId).map(s => s.id));
  }, [focusedProviderId]);
  const providerAggregatedData = useMemo(() => {
    if (!providerServiceIds) return null;
    const services = SERVICES_DATA.filter(s => providerServiceIds.has(s.id));
    const totalCost = services.reduce((sum, s) => sum + s.cost, 0);
    const avgCompliance = Math.round(services.reduce((sum, s) => sum + s.compliance, 0) / services.length);
    const totalRequests = services.reduce((sum, s) => sum + s.requestsPerDay, 0);
    return {
      count: services.length,
      cost: totalCost,
      compliance: avgCompliance,
      requestsPerDay: totalRequests
    };
  }, [providerServiceIds]);
  const nodeTypes = useMemo(() => ({
    serviceNode: ServiceNode,
    engineNode: EngineNode,
    providerNode: ProviderNode
  }), []);
  const edgeTypes = useMemo(() => ({
    animatedEdge: AnimatedEdge
  }), []);
  const onConnect = useCallback((params: Connection) => setEdges(eds => addEdge(params, eds)), [setEdges]);
  const focusedNodeData = useMemo(() => SERVICES_DATA.find(n => n.id === focusedNodeId) || null, [focusedNodeId]);
  const detailNodeData = useMemo(() => SERVICES_DATA.find(n => n.id === detailNodeId) || null, [detailNodeId]);
  const activeProviderId = useMemo(() => {
    if (!focusedNodeData) return null;
    return MODEL_TO_PROVIDER[focusedNodeData.model] || null;
  }, [focusedNodeData]);
  const displayNodes = useMemo(() => {
    if (!focusedNodeId && !focusedProviderId && !filteredServiceIds) {
      return initialNodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          dimmed: false,
          ...(node.id === 'engine' ? {
            focusedService: null
          } : {})
        }
      }));
    }
    if (focusedProviderId && !focusedNodeId) {
      return initialNodes.map(node => {
        if (node.id === 'engine') return {
          ...node,
          data: {
            ...node.data,
            dimmed: false,
            focusedService: null,
            providerAggregate: providerAggregatedData
          }
        };
        if (node.type === 'serviceNode') return {
          ...node,
          data: {
            ...node.data,
            dimmed: !providerServiceIds?.has(node.id)
          }
        };
        if (node.type === 'providerNode') return {
          ...node,
          data: {
            ...node.data,
            dimmed: node.id !== focusedProviderId
          }
        };
        return node;
      });
    }
    if (!focusedNodeId && filteredServiceIds) {
      return initialNodes.map(node => {
        if (node.id === 'engine') return {
          ...node,
          data: {
            ...node.data,
            dimmed: false,
            focusedService: null
          }
        };
        if (node.type === 'serviceNode') return {
          ...node,
          data: {
            ...node.data,
            dimmed: !filteredServiceIds.has(node.id)
          }
        };
        if (node.type === 'providerNode') return {
          ...node,
          data: {
            ...node.data,
            dimmed: !filteredProviderIds?.has(node.id)
          }
        };
        return node;
      });
    }
    return initialNodes.map(node => {
      if (node.id === focusedNodeId) return {
        ...node,
        data: {
          ...node.data,
          dimmed: false
        }
      };
      if (node.id === 'engine') return {
        ...node,
        data: {
          ...node.data,
          dimmed: false,
          focusedService: focusedNodeData
        }
      };
      if (node.id === activeProviderId) return {
        ...node,
        data: {
          ...node.data,
          dimmed: false
        }
      };
      return {
        ...node,
        data: {
          ...node.data,
          dimmed: true
        }
      };
    });
  }, [focusedNodeId, focusedNodeData, activeProviderId, focusedProviderId, providerServiceIds, providerAggregatedData, filteredServiceIds, filteredProviderIds]);
  const modeEdges = useMemo(() => {
    const dotColor = flowMode === 'utilizzo' ? '#ffffff' : '#ef4444';
    const serviceEdges = SERVICES_DATA.map(service => ({
      id: `e-${service.id}-engine`,
      source: service.id,
      target: 'engine',
      type: 'animatedEdge',
      data: {
        intensity: flowMode === 'utilizzo' ? getIntensity(service.requestsPerDay) : getCostIntensity(service.cost),
        dotColor
      },
      style: {
        stroke: flowMode === 'utilizzo' ? 'rgba(255, 255, 255, 0.20)' : 'rgba(239, 68, 68, 0.20)',
        strokeWidth: 1.5
      },
      animated: true
    }));
    const providerEdges = PROVIDERS_DATA.map(provider => ({
      id: `e-engine-${provider.id}`,
      source: 'engine',
      target: provider.id,
      type: 'animatedEdge',
      data: {
        intensity: Math.max(1, Math.round(provider.percentage / 15)),
        dotColor
      },
      style: {
        stroke: flowMode === 'utilizzo' ? 'rgba(255, 255, 255, 0.20)' : 'rgba(239, 68, 68, 0.20)',
        strokeWidth: 1.5
      },
      animated: true
    }));
    return [...serviceEdges, ...providerEdges];
  }, [flowMode]);
  const displayEdges = useMemo(() => {
    if (!focusedNodeId && !focusedProviderId && !filteredServiceIds) {
      return modeEdges.map(edge => ({
        ...edge,
        data: {
          ...edge.data,
          dimmed: false
        }
      }));
    }
    if (focusedProviderId && !focusedNodeId) {
      return modeEdges.map(edge => {
        const isServiceEdge = edge.source.startsWith('s') && edge.target === 'engine';
        const isProviderEdge = edge.source === 'engine' && edge.target.startsWith('p');
        if (isServiceEdge) return {
          ...edge,
          data: {
            ...edge.data,
            dimmed: !providerServiceIds?.has(edge.source)
          }
        };
        if (isProviderEdge) return {
          ...edge,
          data: {
            ...edge.data,
            dimmed: edge.target !== focusedProviderId
          }
        };
        return edge;
      });
    }
    if (!focusedNodeId && filteredServiceIds) {
      return modeEdges.map(edge => {
        const isServiceEdge = edge.source.startsWith('s') && edge.target === 'engine';
        const isProviderEdge = edge.source === 'engine' && edge.target.startsWith('p');
        if (isServiceEdge) return {
          ...edge,
          data: {
            ...edge.data,
            dimmed: !filteredServiceIds.has(edge.source)
          }
        };
        if (isProviderEdge) return {
          ...edge,
          data: {
            ...edge.data,
            dimmed: !filteredProviderIds?.has(edge.target)
          }
        };
        return edge;
      });
    }
    return modeEdges.map(edge => {
      const isActiveServiceEdge = edge.source === focusedNodeId && edge.target === 'engine';
      const isActiveProviderEdge = edge.source === 'engine' && edge.target === activeProviderId;
      return {
        ...edge,
        data: {
          ...edge.data,
          dimmed: !(isActiveServiceEdge || isActiveProviderEdge)
        }
      };
    });
  }, [focusedNodeId, activeProviderId, focusedProviderId, providerServiceIds, modeEdges, filteredServiceIds, filteredProviderIds]);
  useEffect(() => {
    setNodes(displayNodes);
  }, [displayNodes, setNodes]);
  useEffect(() => {
    setEdges(displayEdges);
  }, [displayEdges, setEdges]);
  // Single click → highlight flow only
  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    if (node.type === 'serviceNode') {
      setFocusedProviderId(null);
      setFocusedNodeId(prev => prev === node.id ? null : node.id);
    }
    if (node.type === 'providerNode') {
      setFocusedNodeId(null);
      setFocusedProviderId(prev => prev === node.id ? null : node.id);
    }
  }, []);
  // Double click → drill into service detail canvas
  const onNodeDoubleClick = useCallback((event: React.MouseEvent, node: Node) => {
    if (node.type === 'serviceNode') {
      setDrillServiceId(node.id);
    }
  }, []);
  const onPaneClick = useCallback(() => {
    setFocusedNodeId(null);
    setFocusedProviderId(null);
  }, []);
  // Handle back from drill-down
  const handleBackFromDrill = useCallback(() => {
    setDrillServiceId(null);
  }, []);
  // Handle navigate to detail page
  const navigate = useNavigate();
  const handleNavigateToDetail = useCallback((serviceId: string) => {
    navigate(`/servizio/${serviceId}`);
  }, [navigate]);
  // If we're in drill-down mode, render the ServiceDetailCanvas
  if (drilledService) {
    return <div className="w-full h-[calc(100vh-56px)] bg-[#121212]" data-id="element-1485">
        <ServiceDetailCanvas service={drilledService} onBack={handleBackFromDrill} onNavigateToDetail={handleNavigateToDetail} data-id="element-1486" />
      </div>;
  }
  // Otherwise render the main canvas
  return <div className="w-full h-[calc(100vh-56px)] bg-[#121212] relative" data-id="element-1487">
      {/* Flow Mode Selector & Filters — floating top left */}
      <div className="absolute top-4 left-4 z-20 flex items-center gap-2" data-id="element-1488">
        <div className="relative" data-id="element-1489">
          <button onClick={() => setFlowDropdownOpen(!flowDropdownOpen)} className="flex items-center gap-2 px-3 py-2 bg-[#141414]/95 backdrop-blur-xl border border-white/[0.08] rounded-sm hover:border-white/[0.12] transition-all duration-200 shadow-lg" data-id="element-1490">
            <div className={`w-2 h-2 rounded-full ${flowMode === 'utilizzo' ? 'bg-white' : 'bg-red-500'}`} data-id="element-1491" />
            <span className="text-white/70 text-[12px] font-medium" data-id="element-1492">
              {flowMode === 'utilizzo' ? 'Utilizzo' : 'Consumi'}
            </span>
            <ChevronDown className={`w-3 h-3 text-white/30 transition-transform duration-200 ${flowDropdownOpen ? 'rotate-180' : ''}`} data-id="element-1493" />
          </button>
          {flowDropdownOpen && <>
              <div className="fixed inset-0 z-10" onClick={() => setFlowDropdownOpen(false)} data-id="element-1494" />
              <div className="absolute top-full mt-1.5 left-0 min-w-[160px] bg-[#141414]/95 backdrop-blur-xl border border-white/[0.08] rounded-sm overflow-hidden shadow-xl z-20" data-id="element-1495">
                <button onClick={() => {
              setFlowMode('utilizzo');
              setFlowDropdownOpen(false);
            }} className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-all duration-150 ${flowMode === 'utilizzo' ? 'bg-white/[0.06] text-white' : 'text-white/50 hover:bg-white/[0.04] hover:text-white/70'}`} data-id="element-1496">
                  <div className="w-2 h-2 rounded-full bg-white" data-id="element-1497" />
                  <div data-id="element-1498">
                    <span className="text-[12px] font-medium block" data-id="element-1499">
                      Utilizzo
                    </span>
                    <span className="text-[10px] text-white/30" data-id="element-1500">
                      Requests/day
                    </span>
                  </div>
                </button>
                <div className="h-px bg-white/[0.10]" data-id="element-1501" />
                <button onClick={() => {
              setFlowMode('consumi');
              setFlowDropdownOpen(false);
            }} className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-all duration-150 ${flowMode === 'consumi' ? 'bg-white/[0.06] text-white' : 'text-white/50 hover:bg-white/[0.04] hover:text-white/70'}`} data-id="element-1502">
                  <div className="w-2 h-2 rounded-full bg-red-500" data-id="element-1503" />
                  <div data-id="element-1504">
                    <span className="text-[12px] font-medium block" data-id="element-1505">
                      Consumi
                    </span>
                    <span className="text-[10px] text-white/30" data-id="element-1506">
                      Costo mensile (€)
                    </span>
                  </div>
                </button>
              </div>
            </>}
        </div>
        <div className="relative" data-id="element-1507">
          <button onClick={() => setFiltersOpen(!filtersOpen)} className={`flex items-center gap-2 px-3 py-2 bg-[#141414]/95 backdrop-blur-xl border rounded-sm transition-all duration-200 shadow-lg ${healthFilter.size > 0 ? 'border-white/[0.12] text-white' : 'border-white/[0.08] text-white/50 hover:text-white/70 hover:border-white/[0.12]'}`} data-id="element-1508">
            <SlidersHorizontal className="w-3.5 h-3.5" data-id="element-1509" />
            <span className="text-[12px] font-medium" data-id="element-1510">Filtri</span>
            {healthFilter.size > 0 && <span className="w-4 h-4 rounded-full bg-white/[0.15] text-[10px] font-semibold flex items-center justify-center" data-id="element-1511">
                {healthFilter.size}
              </span>}
          </button>
          {filtersOpen && <>
              <div className="fixed inset-0 z-10" onClick={() => setFiltersOpen(false)} data-id="element-1512" />
              <div className="absolute top-full mt-1.5 left-0 min-w-[200px] bg-[#141414]/95 backdrop-blur-xl border border-white/[0.08] rounded-sm overflow-hidden shadow-xl z-20" data-id="element-1513">
                <div className="px-3 py-2.5 border-b border-white/[0.06]" data-id="element-1514">
                  <span className="text-[10px] font-semibold text-white/35 uppercase tracking-wider" data-id="element-1515">
                    Stato Salute
                  </span>
                </div>
                {[{
              status: 'healthy' as HealthStatus,
              color: 'bg-[#8eff01]',
              label: 'Healthy'
            }, {
              status: 'warning' as HealthStatus,
              color: 'bg-amber-500',
              label: 'Warning'
            }, {
              status: 'critical' as HealthStatus,
              color: 'bg-red-500',
              label: 'Critical'
            }].map(({
              status,
              color,
              label
            }) => {
              const isActive = healthFilter.has(status);
              return <button key={status} onClick={() => toggleHealthFilter(status)} className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-all duration-150 ${isActive ? 'bg-white/[0.06] text-white' : 'text-white/50 hover:bg-white/[0.04] hover:text-white/70'}`} data-id="element-1516">
                      <div className={`w-2 h-2 rounded-full ${color} ${isActive ? '' : 'opacity-40'}`} data-id="element-1517" />
                      <span className="text-[12px] font-medium flex-1" data-id="element-1518">
                        {label}
                      </span>
                      {isActive && <div className="w-3.5 h-3.5 rounded bg-white/[0.10] flex items-center justify-center" data-id="element-1519">
                          <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none" data-id="element-1520">
                            <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-1521" />
                          </svg>
                        </div>}
                    </button>;
            })}
                {healthFilter.size > 0 && <>
                    <div className="h-px bg-white/[0.10]" data-id="element-1522" />
                    <button onClick={() => {
                setHealthFilter(new Set());
                setFocusedNodeId(null);
              }} className="w-full px-3 py-2.5 text-left text-[12px] font-medium text-white/30 hover:text-white/50 hover:bg-white/[0.02] transition-all duration-150" data-id="element-1523">
                      Rimuovi filtri
                    </button>
                  </>}
              </div>
            </>}
        </div>
      </div>

      {/* Date Range Filter — floating top right */}
      <div className="absolute top-4 right-4 z-20" data-id="element-1524">
        <div className="relative" data-id="element-1525">
          <button onClick={() => setDateDropdownOpen(!dateDropdownOpen)} className="flex items-center gap-2 px-3 py-2 bg-[#141414]/95 backdrop-blur-xl border border-white/[0.08] rounded-sm hover:border-white/[0.12] transition-all duration-200 shadow-lg" data-id="element-1526">
            <Calendar className="w-3.5 h-3.5 text-white/40" data-id="element-1527" />
            <span className="text-white/70 text-[12px] font-medium" data-id="element-1528">
              {selectedDateLabel}
            </span>
            <ChevronDown className={`w-3 h-3 text-white/30 transition-transform duration-200 ${dateDropdownOpen ? 'rotate-180' : ''}`} data-id="element-1529" />
          </button>
          {dateDropdownOpen && <>
              <div className="fixed inset-0 z-10" onClick={() => setDateDropdownOpen(false)} data-id="element-1530" />
              <div className="absolute top-full mt-1.5 right-0 min-w-[180px] bg-[#141414]/95 backdrop-blur-xl border border-white/[0.08] rounded-sm overflow-hidden shadow-xl z-20" data-id="element-1531">
                {dateOptions.map((option, index) => <Fragment key={option.id} data-id="element-1532">
                    {index > 0 && <div className="h-px bg-white/[0.08]" data-id="element-1533" />}
                    <button onClick={() => {
                setDateRange(option.id);
                setDateDropdownOpen(false);
              }} className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-all duration-150 ${dateRange === option.id ? 'bg-white/[0.06] text-white' : 'text-white/50 hover:bg-white/[0.04] hover:text-white/70'}`} data-id="element-1534">
                      <span className="text-[12px] font-medium" data-id="element-1535">
                        {option.label}
                      </span>
                    </button>
                  </Fragment>)}
              </div>
            </>}
        </div>
      </div>

      <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect} onNodeClick={onNodeClick} onNodeDoubleClick={onNodeDoubleClick} onPaneClick={onPaneClick} nodeTypes={nodeTypes} edgeTypes={edgeTypes} fitView className="bg-[#121212]" minZoom={0.5} maxZoom={2} fitViewOptions={{
      padding: 0.1,
      minZoom: 0.7,
      maxZoom: 1.2
    }} data-id="element-1536">
        <Background color="rgba(255, 255, 255, 0.30)" gap={20} variant={BackgroundVariant.Dots} size={1.5} data-id="element-1537" />
        <Controls className="bg-[#141414] border border-white/[0.08] text-white fill-white" data-id="element-1538" />
        <MiniMap nodeStrokeColor="#333" nodeColor={n => {
        if (n.type === 'serviceNode') return '#1e1e1e';
        if (n.type === 'engineNode') return '#8eff01';
        return '#0a0a0a';
      }} maskColor="rgba(0, 0, 0, 0.7)" className="bg-[#0a0a0a] border border-white/[0.08]" data-id="element-1539" />
      </ReactFlow>

      {/* Right Side Panel — opens on double click (legacy, now replaced by drill-down) */}
      <RightPanel isOpen={!!detailNodeData} onClose={() => setDetailNodeId(null)} title={detailNodeData?.name || ''} subtitle={detailNodeData?.type || ''} width="md" data-id="element-1540">
        {detailNodeData && <div className="flex flex-col h-full" data-id="element-1541">
            <div className="flex gap-0 border-b border-white/[0.06] px-6" data-id="element-1542">
              {[{
            id: 'overview',
            label: 'Overview'
          }, {
            id: 'compliance',
            label: 'Compliance'
          }, {
            id: 'consumi',
            label: 'Usage'
          }].map(tab => <button key={tab.id} onClick={() => setPanelTab(tab.id as any)} className={`px-4 py-3.5 text-[12px] font-medium transition-all relative ${panelTab === tab.id ? 'text-white' : 'text-white/30 hover:text-white/50'}`} data-id="element-1543">
                  {tab.label}
                  {panelTab === tab.id && <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-[#8eff01] rounded-full" data-id="element-1544" />}
                </button>)}
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-5" data-id="element-1545">
              {panelTab === 'overview' && <div className="space-y-5" data-id="element-1546">
                  <div className="p-4 bg-white/[0.02] rounded-[12px] border border-white/[0.06]" data-id="element-1547">
                    <p className="text-[10px] font-medium text-white/30 uppercase tracking-wider mb-2" data-id="element-1548">
                      Status
                    </p>
                    <div className="flex items-center gap-2" data-id="element-1549">
                      <div className={`w-2 h-2 rounded-full ${detailNodeData.health === 'healthy' ? 'bg-[#8eff01]' : detailNodeData.health === 'warning' ? 'bg-amber-500' : 'bg-red-500'}`} data-id="element-1550" />
                      <span className="text-[15px] font-medium text-white capitalize" data-id="element-1551">
                        {detailNodeData.health}
                      </span>
                    </div>
                  </div>
                  <div data-id="element-1552">
                    <p className="text-[11px] font-medium text-white/30 uppercase tracking-wider mb-2" data-id="element-1553">
                      Description
                    </p>
                    <p className="text-[13px] text-white/50 leading-relaxed" data-id="element-1554">
                      {detailNodeData.description}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3" data-id="element-1555">
                    <div className="p-3 bg-white/[0.02] rounded-[8px] border border-white/[0.05]" data-id="element-1556">
                      <p className="text-[10px] text-white/25 mb-1" data-id="element-1557">Model</p>
                      <div className="flex items-center gap-1.5" data-id="element-1558">
                        <Cpu className="w-3 h-3 text-white/30" data-id="element-1559" />
                        <span className="text-[12px] font-medium text-white/70" data-id="element-1560">
                          {detailNodeData.model}
                        </span>
                      </div>
                    </div>
                    <div className="p-3 bg-white/[0.02] rounded-[8px] border border-white/[0.05]" data-id="element-1561">
                      <p className="text-[10px] text-white/25 mb-1" data-id="element-1562">Type</p>
                      <span className="text-[12px] font-medium text-white/70" data-id="element-1563">
                        {detailNodeData.type}
                      </span>
                    </div>
                  </div>
                </div>}
              {panelTab === 'compliance' && <div className="space-y-5" data-id="element-1564">
                  <div className="text-center p-6 bg-white/[0.02] rounded-[12px] border border-white/[0.06]" data-id="element-1565">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/[0.03] mb-4 relative" data-id="element-1566">
                      <span className={`text-[24px] font-bold ${detailNodeData.compliance >= 90 ? 'text-[#8eff01]' : 'text-amber-400'}`} data-id="element-1567">
                        {detailNodeData.compliance}
                      </span>
                      <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 36 36" data-id="element-1568">
                        <circle cx="18" cy="18" r="16" fill="none" stroke="white" strokeOpacity="0.05" strokeWidth="2" data-id="element-1569" />
                        <circle cx="18" cy="18" r="16" fill="none" stroke={detailNodeData.compliance >= 90 ? '#8eff01' : '#f59e0b'} strokeWidth="2" strokeDasharray={`${detailNodeData.compliance}, 100`} strokeLinecap="round" data-id="element-1570" />
                      </svg>
                    </div>
                    <p className="text-[11px] text-white/30 uppercase tracking-wider" data-id="element-1571">
                      AI Act Compliance
                    </p>
                  </div>
                  <div className="space-y-2" data-id="element-1572">
                    {[{
                label: 'Risk Classification',
                value: 'Limited Risk',
                ok: true
              }, {
                label: 'Data Privacy',
                value: 'GDPR Compliant',
                ok: true
              }, {
                label: 'Human Oversight',
                value: detailNodeData.compliance >= 90 ? 'Active' : 'Required',
                ok: detailNodeData.compliance >= 90
              }].map((item, i) => <div key={i} className="flex items-center justify-between p-3 bg-white/[0.02] rounded-[8px] border border-white/[0.05]" data-id="element-1573">
                        <span className="text-[12px] text-white/50" data-id="element-1574">
                          {item.label}
                        </span>
                        <AppBadge variant={item.ok ? 'green' : 'amber'} data-id="element-1575">
                          {item.value}
                        </AppBadge>
                      </div>)}
                  </div>
                </div>}
              {panelTab === 'consumi' && <div className="space-y-5" data-id="element-1576">
                  <div className="grid grid-cols-2 gap-3" data-id="element-1577">
                    <div className="p-4 bg-white/[0.02] rounded-[12px] border border-white/[0.06]" data-id="element-1578">
                      <div className="flex items-center gap-2 mb-2" data-id="element-1579">
                        <TrendingUp className="w-3.5 h-3.5 text-white/25" data-id="element-1580" />
                        <span className="text-[10px] text-white/30 uppercase tracking-wider" data-id="element-1581">
                          Monthly Cost
                        </span>
                      </div>
                      <p className="text-[22px] font-semibold text-white" data-id="element-1582">
                        €{detailNodeData.cost}
                      </p>
                    </div>
                    <div className="p-4 bg-white/[0.02] rounded-[12px] border border-white/[0.06]" data-id="element-1583">
                      <div className="flex items-center gap-2 mb-2" data-id="element-1584">
                        <Activity className="w-3.5 h-3.5 text-white/25" data-id="element-1585" />
                        <span className="text-[10px] text-white/30 uppercase tracking-wider" data-id="element-1586">
                          Requests/Day
                        </span>
                      </div>
                      <p className="text-[22px] font-semibold text-white" data-id="element-1587">
                        {detailNodeData.requestsPerDay.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="p-4 bg-white/[0.02] rounded-[12px] border border-white/[0.06]" data-id="element-1588">
                    <p className="text-[11px] font-medium text-white/30 uppercase tracking-wider mb-4" data-id="element-1589">
                      Environmental Impact
                    </p>
                    <div className="space-y-4" data-id="element-1590">
                      <div data-id="element-1591">
                        <div className="flex justify-between text-[12px] mb-1.5" data-id="element-1592">
                          <span className="text-white/40" data-id="element-1593">CO₂ Emissions</span>
                          <span className="text-white/70 font-medium" data-id="element-1594">
                            {detailNodeData.co2}
                          </span>
                        </div>
                        <div className="w-full bg-white/[0.05] rounded-full h-1" data-id="element-1595">
                          <div className="bg-[#8eff01] h-1 rounded-full" style={{
                      width: '45%'
                    }} data-id="element-1596" />
                        </div>
                      </div>
                      <div data-id="element-1597">
                        <div className="flex justify-between text-[12px] mb-1.5" data-id="element-1598">
                          <span className="text-white/40" data-id="element-1599">Token Usage</span>
                          <span className="text-white/70 font-medium" data-id="element-1600">
                            {detailNodeData.tokens}
                          </span>
                        </div>
                        <div className="w-full bg-white/[0.05] rounded-full h-1" data-id="element-1601">
                          <div className="bg-blue-500 h-1 rounded-full" style={{
                      width: '60%'
                    }} data-id="element-1602" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
            <div className="p-5 border-t border-white/[0.06] space-y-2.5" data-id="element-1603">
              <AppButton fullWidth icon={<FileText className="w-3.5 h-3.5" data-id="element-1605" />} data-id="element-1604">
                Generate Report
              </AppButton>
              <div className="grid grid-cols-2 gap-2" data-id="element-1606">
                <AppButton variant="secondary" fullWidth icon={<Search className="w-3.5 h-3.5" data-id="element-1608" />} data-id="element-1607">
                  Audit
                </AppButton>
                <AppButton variant="secondary" fullWidth icon={<Settings className="w-3.5 h-3.5" data-id="element-1610" />} data-id="element-1609">
                  Configure
                </AppButton>
              </div>
            </div>
          </div>}
      </RightPanel>
    </div>;
}